import {
  Color,
  DoubleSide,
  Group,
  MathUtils,
  Mesh,
  Quaternion,
  ShaderMaterial,
  Vector2,
  Vector3,
  type Object3D,
} from 'three'
import {mergeBufferGeometries} from 'three/examples/jsm/utils/BufferGeometryUtils'

import type {MonaColorVariant} from '../config'
import eyeFragmentShader from './_shaders/mona-eye-frag'
import faceFragmentShader from './_shaders/mona-face-frag'
import fragmentShader from './_shaders/mona-frag'
import legVertexShader from './_shaders/mona-leg-vert'
import vertexShader from './_shaders/mona-vert'

const X_AXIS = new Vector3(1, 0, 0)
const TENTACLE_TRAIL_STRENGTH = 5
const TENTACLE_RESTING_TILT = 0.8
const TENTACLE_TILT_SMOOTHING = 2

const COLOR_VARIANT_DEFINES = {
  black: 'MONA_COLOR_BLACK',
  purple: 'MONA_COLOR_PURPLE',
  pearl: 'MONA_COLOR_PEARL',
} satisfies Record<MonaColorVariant, string>

function srgbChannelToLinear(channel: number) {
  return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4
}

function setLinearColorFromSRGBHex(color: Color, hex: number) {
  color.r = srgbChannelToLinear(((hex >> 16) & 255) / 255)
  color.g = srgbChannelToLinear(((hex >> 8) & 255) / 255)
  color.b = srgbChannelToLinear((hex & 255) / 255)
  return color
}

function linearColorFromSRGBHex(hex: number) {
  return setLinearColorFromSRGBHex(new Color(), hex)
}

export default class MainMona {
  group = new Group()
  group2 = new Group()
  monaHeadGroup = new Group()
  tentaclesGroup = new Group()
  commonUniforms = {
    uResolution: {value: new Vector2()},
    uTime: {value: 0},
  }
  legRandomScalarUniforms: [{value: number}, {value: number}, {value: number}, {value: number}, {value: number}] = [
    {value: 0},
    {value: 1 / 5},
    {value: 2 / 5},
    {value: 3 / 5},
    {value: 4 / 5},
  ]
  mouseMovementUniform = {value: 0}
  verticalTiltUniform = {value: 0}
  facingQuaternion = new Quaternion()
  verticalTiltQuaternion = new Quaternion()
  horizontalTiltQuaternion = new Quaternion()
  restingPitch = -0.2
  verticalTiltStrength = 2
  colorVariant: MonaColorVariant

  constructor(colorVariant: MonaColorVariant = 'black') {
    this.colorVariant = colorVariant
    this.group.add(this.group2)
    this.group2.add(this.monaHeadGroup)
    this.group2.add(this.tentaclesGroup)
  }

  setResolution(width: number, height: number) {
    this.commonUniforms.uResolution.value.set(width, height)
  }

  create(scene: Object3D | Group) {
    const eyeGroup = new Group()
    eyeGroup.name = 'EyeGroup'
    this.monaHeadGroup.add(eyeGroup)

    const legMeshGroup = new Group()
    legMeshGroup.userData.isFront = false
    this.tentaclesGroup.add(legMeshGroup)

    scene.traverse(child => {
      if (child.name === 'MonaHead') {
        this.monaHeadGroup.position.copy(child.position)
      }

      if (!(child instanceof Mesh)) return

      const geometry = child.geometry
      const material = this.createMaterial(child.name, this.legRandomScalarUniforms[0])

      const mirroredGeometry = geometry.clone()
      mirroredGeometry.scale(-1, 1, 1)

      let mesh: Mesh

      switch (child.name) {
        case 'Pupil_Highlight':
          mesh = new Mesh(geometry, material)
          mesh.position.copy(child.position)
          mesh.rotation.copy(child.rotation)
          this.monaHeadGroup.add(mesh)
          break
        case 'Eye':
        case 'Eyelash':
        case 'Pupil':
          mesh = new Mesh(geometry, material)
          mesh.position.copy(child.position)
          mesh.rotation.copy(child.rotation)
          mesh.name = child.name
          eyeGroup.add(mesh)
          break
        case 'Nose':
        case 'Mouth':
        case 'Head':
        case 'Face':
        case 'FaceSmile':
        case 'FaceSmile2': {
          const combinedGeometry = mergeBufferGeometries([geometry, mirroredGeometry])
          if (!combinedGeometry) {
            throw new Error(`Unable to merge geometry for ${child.name}`)
          }

          mesh = new Mesh(combinedGeometry, material)
          mesh.position.copy(child.position)
          mesh.rotation.copy(child.rotation)
          mesh.name = child.name
          this.monaHeadGroup.add(mesh)
          break
        }
        case 'Leg_1':
        case 'Leg_2': {
          const combinedGeometry = mergeBufferGeometries([geometry, mirroredGeometry])
          if (!combinedGeometry) {
            throw new Error(`Unable to merge geometry for ${child.name}`)
          }

          mesh = new Mesh(combinedGeometry, material)
          mesh.position.copy(child.position)
          mesh.rotation.copy(child.rotation)
          mesh.name = child.name
          mesh.userData.isFront = false
          legMeshGroup.add(mesh)
          break
        }
      }
    })

    const mirroredEyeGroup = eyeGroup.clone()
    mirroredEyeGroup.name = 'EyeGroup_mirrored'
    mirroredEyeGroup.scale.set(-1, 1, 1)
    this.monaHeadGroup.add(mirroredEyeGroup)

    for (let i = 1; i < 5; i++) {
      const clonedGroup = legMeshGroup.clone()
      const isFront = i === 2 || i === 3
      const legRandomScalar = this.legRandomScalarUniforms[i]
      if (!legRandomScalar) {
        throw new Error(`Missing leg animation scalar for index ${i}`)
      }

      clonedGroup.userData.isFront = isFront
      clonedGroup.traverse(child => {
        child.userData.isFront = isFront
        if (child instanceof Mesh) {
          child.material = this.createMaterial(child.name, legRandomScalar, i)
        }
      })
      // clonedGroup.rotation.y = i / 5 * Math.PI * 2;
      this.tentaclesGroup.add(clonedGroup)
    }
  }

  createMaterial(name: string, legRandomScalar = this.legRandomScalarUniforms[0], index = 0) {
    const defines = {
      [COLOR_VARIANT_DEFINES[this.colorVariant]]: 1,
      ...(name === 'Head' || name === 'Leg_2' ? {IS_BODY_COLOR: 1} : {}),
    }
    const uniforms = {
      uBaseColor: {value: linearColorFromSRGBHex(0x180e27)},
      uLightDirection: {value: new Vector3(0.1, 1.0, 1.0).normalize()},
      ...this.commonUniforms,
    }

    switch (name) {
      case 'Eye':
        setLinearColorFromSRGBHex(uniforms.uBaseColor.value, 0xdfd4ee)
        break
      case 'Eyelash':
        setLinearColorFromSRGBHex(uniforms.uBaseColor.value, 0x3b224e)
        break
      case 'Pupil_Highlight':
        setLinearColorFromSRGBHex(uniforms.uBaseColor.value, 0xffffff)
        break
      case 'Leg_1':
        setLinearColorFromSRGBHex(uniforms.uBaseColor.value, 0x905ffb)
        break
      case 'Leg_2':
        setLinearColorFromSRGBHex(uniforms.uBaseColor.value, 0x180e27)
        break
    }

    switch (name) {
      case 'Pupil':
        setLinearColorFromSRGBHex(uniforms.uBaseColor.value, 0x0b041f)
        return new ShaderMaterial({
          vertexShader,
          fragmentShader: eyeFragmentShader,
          transparent: true,
          side: DoubleSide,
          uniforms: {
            ...uniforms,
            uOutlineColor: {value: linearColorFromSRGBHex(0x332075)},
          },
          defines,
        })
      case 'Face':
      case 'FaceSmile':
      case 'FaceSmile2':
        return new ShaderMaterial({
          vertexShader,
          fragmentShader: faceFragmentShader,
          transparent: true,
          side: DoubleSide,
          uniforms: {
            ...uniforms,
            uColor1: {value: linearColorFromSRGBHex(0xffafc6)},
            uColor2: {value: linearColorFromSRGBHex(0xe99e89)},
            uColor3: {value: linearColorFromSRGBHex(0xe979a1)},
            uColor4: {value: linearColorFromSRGBHex(0xf88580)},
          },
          defines,
        })
      case 'Leg_1':
      case 'Leg_2':
        return new ShaderMaterial({
          vertexShader: legVertexShader,
          fragmentShader,
          transparent: true,
          side: DoubleSide,
          uniforms: {
            ...uniforms,
            uRandomScalar: legRandomScalar,
            uMouseMovementAmount: this.mouseMovementUniform,
            uVerticalTilt: this.verticalTiltUniform,
            uIsFront: {value: index === 2 || index === 3},
            uIndex: {value: index},
          },
          defines,
        })

      default:
        return new ShaderMaterial({
          vertexShader,
          fragmentShader,
          transparent: true,
          side: DoubleSide,
          uniforms,
          defines,
        })
    }
  }

  applyRotation() {
    this.group.quaternion
      .copy(this.horizontalTiltQuaternion)
      .multiply(this.verticalTiltQuaternion)
      .multiply(this.facingQuaternion)
  }

  updateRestingPose(delta: number) {
    this.facingQuaternion.identity()
    this.verticalTiltQuaternion.setFromAxisAngle(X_AXIS, this.restingPitch)
    this.horizontalTiltQuaternion.identity()
    this.verticalTiltUniform.value = 0
    this.applyRotation()
    this.updateMovement(0, delta)
  }

  updateMovement(mouseMovementAmount: number, delta: number) {
    this.mouseMovementUniform.value = mouseMovementAmount
    const tentacleTiltTarget = Math.max(TENTACLE_RESTING_TILT, mouseMovementAmount * TENTACLE_TRAIL_STRENGTH)
    const tentacleTiltSmoothing = 1 - Math.exp(-delta * TENTACLE_TILT_SMOOTHING)
    this.tentaclesGroup.rotation.x = MathUtils.lerp(
      this.tentaclesGroup.rotation.x,
      tentacleTiltTarget,
      tentacleTiltSmoothing,
    )
  }

  update() {}
}
